// MediaUpload — editor control: a thumbnail grid of an entity's media with a
// dashed "+" tile to add more, a caption input under each image, and a remove
// button. Clicking a thumbnail opens the shared lightbox preview. Each file is
// read by the browser and POSTed to /api/media (raw bytes + X-Filename); the
// returned URL is appended to media_urls. Consumers own the persisted state
// (urls + a URL→caption map) and patch on every onChange.
//
// Captions are edited in a local draft and committed on blur, so typing a
// caption fires one patch (on blur) instead of one per keystroke. The draft is
// re-seeded whenever the server-side caption map changes.
import { useEffect, useRef, useState } from "react";
import { useUploadMedia } from "@/api/mutations";
import { ApiError } from "@/api/client";
import { Spinner } from "@/ui/spinner";
import { ImageLightbox, VIDEO_RE, toMediaItems } from "./MediaGallery";

export function MediaUpload({
  urls,
  captions,
  onChange,
}: {
  urls: string[];
  captions: Record<string, string>;
  onChange: (urls: string[], captions: Record<string, string>) => void;
}) {
  const upload = useUploadMedia();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [err, setErr] = useState<string>("");
  const [preview, setPreview] = useState<number | null>(null);

  // Local caption edits, committed to the parent on blur. Re-seed when the
  // persisted map changes (e.g. a save round-trips or another image is added).
  const [captionDraft, setCaptionDraft] = useState<Record<string, string>>(captions);
  const captionsKey = JSON.stringify(captions);
  useEffect(() => {
    setCaptionDraft(captions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsKey]);

  // Strip blank captions so the stored map stays tidy.
  const clean = (m: Record<string, string>): Record<string, string> =>
    Object.fromEntries(Object.entries(m).filter(([, v]) => v.trim() !== ""));

  const onFiles = async (files: FileList) => {
    setErr("");
    const chosen = Array.from(files);
    setUploading((n) => n + chosen.length);
    try {
      const added: string[] = [];
      for (const file of chosen) {
        try {
          const { url } = await upload.mutateAsync(file);
          added.push(url);
        } catch (e) {
          setErr(
            e instanceof ApiError ? e.detail :
            e instanceof Error ? e.message : String(e),
          );
        } finally {
          setUploading((n) => Math.max(0, n - 1));
        }
      }
      if (added.length) onChange([...urls, ...added], clean(captionDraft));
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx: number) => {
    const url = urls[idx];
    const nextUrls = urls.filter((_, i) => i !== idx);
    const nextCaps = { ...captionDraft };
    delete nextCaps[url];
    setCaptionDraft(nextCaps);
    onChange(nextUrls, clean(nextCaps));
  };

  const commitCaptions = () => onChange(urls, clean(captionDraft));

  const items = toMediaItems(urls, captionDraft);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-3">
        {urls.map((u, i) => (
          <div key={u} className="w-32 space-y-1">
            <div className="group relative h-24 w-32 overflow-hidden rounded-md border border-border bg-bg">
              <button
                type="button"
                onClick={() => setPreview(i)}
                className="block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                title="Preview"
              >
                {VIDEO_RE.test(u) ? (
                  <video
                    src={u}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                )}
                {VIDEO_RE.test(u) && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl text-white/90 drop-shadow">
                    ▶
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label="Remove media"
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-xs text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
              >
                ×
              </button>
            </div>
            <input
              value={captionDraft[u] ?? ""}
              onChange={(e) =>
                setCaptionDraft((m) => ({ ...m, [u]: e.target.value }))
              }
              onBlur={commitCaptions}
              placeholder="Caption…"
              className="w-32 rounded border border-border bg-bg px-1.5 py-1 text-[11px] text-text placeholder:text-text-tertiary focus:border-border-hover focus:outline-none"
            />
          </div>
        ))}

        {/* Uploading placeholders */}
        {Array.from({ length: uploading }).map((_, i) => (
          <div
            key={`up-${i}`}
            className="flex h-24 w-32 items-center justify-center rounded-md border border-border bg-bg text-text-tertiary"
          >
            <Spinner className="text-accent" />
          </div>
        ))}

        {/* Add tile */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-24 w-32 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-hover bg-bg text-text-tertiary transition-colors hover:border-text/40 hover:text-text"
          title="Add image or video"
        >
          <span className="text-2xl leading-none">+</span>
          <span className="text-[11px]">Add media</span>
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
        }}
      />

      {err && <p className="text-xs text-destructive">{err}</p>}

      <ImageLightbox items={items} index={preview} onIndex={setPreview} />
    </div>
  );
}
